/**
 * @file 게시판 글 댓글 — 목록 + 입력.
 *
 * 시안과 같은 구성: 아바타 · 이름 · 시간 · 본문 · ⋮ 메뉴, 그 아래 입력 박스(우하단 {@code 0 / 2000}
 * 카운터 + '등록' 버튼). 익명 게시판이면 서버가 작성자를 "익명" 으로 내려주므로 화면은 그대로 그린다.
 */
import React, { useRef, useState } from "react";
import {
    Avatar, Box, IconButton, Menu, MenuItem, Stack, TextField, Tooltip, Typography,
} from "@mui/material";
import MoreVertRoundedIcon from "@mui/icons-material/MoreVertRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";
import ChatBubbleOutlineRoundedIcon from "@mui/icons-material/ChatBubbleOutlineRounded";
import AttachFileRoundedIcon from "@mui/icons-material/AttachFileRounded";
import ActionButton from "../../../component/common/ActionButton";
import AttachmentPreviewList from "../../../component/common/AttachmentPreviewList";
import { confirm as confirmDialog } from "../../../util/confirmDialog";
import { runApiAction, getApiErrorMessage, serverMsgOr } from "../../../util/apiFeedback";
import { notify } from "../../../util/toast";
import { boardApi } from "../api/boardApi";
import { C, formatCommentTime } from "../boardShared";

/** 서버 {@code BoardCommentDtos.SaveRequest} 의 @Size 와 같은 값이어야 한다. */
const MAX_LEN = 2000;

export default function BoardCommentSection({ postId, comments = [], myName, onRefresh }) {
    return (
        <Box>
            {/* 아래 댓글 행·입력 박스와 같은 px 로 맞춘다 — 헤더만 왼쪽으로 튀어나와 보이지 않게. */}
            <Stack direction="row" alignItems="center" spacing={0.75} sx={{ px: 2, pb: 1 }}>
                <ChatBubbleOutlineRoundedIcon sx={{ fontSize: 16, color: C.textMuted }} />
                <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: C.textSecondary }}>댓글</Typography>
                {comments.length > 0 && (
                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: C.primary }}>{comments.length}</Typography>
                )}
            </Stack>

            {comments.map((c, idx) => (
                <CommentRow key={c.id} comment={c} postId={postId} onRefresh={onRefresh}
                    /* 행 사이만 옅은 선으로 나눈다 — 첫 행을 회색으로 칠하면 흰 카드에 얼룩처럼 남는다. */
                    divided={idx > 0} />
            ))}

            <CommentInput postId={postId} myName={myName} onRefresh={onRefresh} />
        </Box>
    );
}

function CommentRow({ comment, postId, onRefresh, divided }) {
    const [menuAnchor, setMenuAnchor] = useState(null);
    const [editing, setEditing] = useState(false);
    const [text, setText] = useState(comment.content || "");
    const fileInputRef = useRef();

    const handleSave = () => {
        const v = text.trim();
        if (!v) { notify("댓글 내용을 입력하세요", "error"); return; }
        return runApiAction({
            request: () => boardApi.updateComment(comment.id, v),
            errorMsg: serverMsgOr("댓글 수정 실패"),
            onSuccess: () => { setEditing(false); onRefresh(); },
        });
    };

    const handleDelete = async () => {
        setMenuAnchor(null);
        if (!(await confirmDialog({ message: "댓글을 삭제하시겠습니까?\n첨부한 파일도 함께 삭제됩니다." }))) return;
        return runApiAction({
            request: () => boardApi.deleteComment(comment.id),
            errorMsg: serverMsgOr("댓글 삭제 실패"),
            onSuccess: onRefresh,
        });
    };

    const handleUpload = (e) => {
        const file = e.target.files?.[0];
        e.target.value = "";
        if (!file) return;
        return runApiAction({
            request: () => boardApi.uploadAttachment(postId, file, comment.id),
            errorMsg: serverMsgOr("파일 업로드 실패"),
            onSuccess: onRefresh,
        });
    };

    const handleDeleteAttachment = async (attachmentId) => {
        if (!(await confirmDialog({ message: "첨부파일을 삭제하시겠습니까?" }))) return;
        return runApiAction({
            request: () => boardApi.deleteAttachment(attachmentId),
            errorMsg: serverMsgOr("파일 삭제 실패"),
            onSuccess: onRefresh,
        });
    };

    return (
        <Stack direction="row" spacing={1.5} sx={{
            px: 2, py: 1.75,
            borderTop: divided ? `1px solid ${C.borderLight}` : "none",
        }}>
            <Avatar sx={{ width: 32, height: 32, fontSize: 13, flexShrink: 0, bgcolor: C.primarySoft }}>
                {comment.authorName?.[0] ?? "?"}
            </Avatar>
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: C.textPrimary }}>
                        {comment.authorName}
                    </Typography>
                    {comment.authorPositionName && (
                        <Typography sx={{ fontSize: 12, color: C.textMuted }}>{comment.authorPositionName}</Typography>
                    )}
                    <Typography sx={{ fontSize: 12, color: C.textMuted }}>
                        {formatCommentTime(comment.createdAt)}
                    </Typography>
                    {comment.updatedAt && (
                        <Typography sx={{ fontSize: 11, color: "#cbd5e1" }}>(수정됨)</Typography>
                    )}
                </Stack>

                {editing ? (
                    <Stack spacing={0.75} mt={0.75}>
                        <TextField
                            multiline minRows={2} fullWidth size="small"
                            value={text}
                            onChange={e => setText(e.target.value.slice(0, MAX_LEN))}
                            sx={{ "& .MuiOutlinedInput-root": { fontSize: 13.5, bgcolor: C.surface } }}
                        />
                        <Stack direction="row" spacing={0.75}>
                            <ActionButton variant="save" startIcon={false} onClick={handleSave}
                                sx={{ fontSize: 12.5, py: 0.3 }} />
                            <ActionButton variant="cancel"
                                onClick={() => { setEditing(false); setText(comment.content || ""); }}
                                sx={{ fontSize: 12.5, py: 0.3 }} />
                        </Stack>
                    </Stack>
                ) : (
                    <Typography sx={{
                        fontSize: 13.5, color: "#334155", lineHeight: 1.7, mt: 0.3,
                        // 붙여쓴 URL·긴 파일명이 카드를 밀어내지 않게. 줄바꿈은 입력 그대로 보존.
                        whiteSpace: "pre-wrap", wordBreak: "break-word",
                    }}>
                        {comment.content}
                    </Typography>
                )}

                {comment.attachments?.length > 0 && (
                    <AttachmentPreviewList
                        attachments={comment.attachments}
                        previewFn={boardApi.previewAttachment}
                        onDelete={comment.editable ? handleDeleteAttachment : undefined}
                        sx={{ mt: 1 }}
                    />
                )}
            </Box>

            {/* 수정·삭제는 본인 또는 관리자에게만 — editable 은 서버가 판단해 내려준다. */}
            {comment.editable && !editing && (
                <>
                    <IconButton size="small" onClick={e => setMenuAnchor(e.currentTarget)}
                        sx={{ p: 0.4, flexShrink: 0, alignSelf: "flex-start" }}>
                        <MoreVertRoundedIcon sx={{ fontSize: 18, color: C.textMuted }} />
                    </IconButton>
                    <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}
                        transformOrigin={{ horizontal: "right", vertical: "top" }}
                        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
                        slotProps={{ paper: { sx: { borderRadius: 1.5, minWidth: 130 } } }}>
                        <MenuItem onClick={() => { setEditing(true); setMenuAnchor(null); }} sx={{ fontSize: 13, gap: 1 }}>
                            <EditRoundedIcon sx={{ fontSize: 16 }} /> 수정
                        </MenuItem>
                        <MenuItem onClick={() => { fileInputRef.current?.click(); setMenuAnchor(null); }} sx={{ fontSize: 13, gap: 1 }}>
                            <AttachFileRoundedIcon sx={{ fontSize: 16 }} /> 파일 첨부
                        </MenuItem>
                        <MenuItem onClick={handleDelete} sx={{ fontSize: 13, gap: 1, color: C.error }}>
                            <DeleteRoundedIcon sx={{ fontSize: 16 }} /> 삭제
                        </MenuItem>
                    </Menu>
                    <input ref={fileInputRef} type="file" hidden onChange={handleUpload} />
                </>
            )}
        </Stack>
    );
}

/** 댓글 입력 — 아바타 + 박스(우하단 글자수) + '등록'. */
function CommentInput({ postId, myName, onRefresh }) {
    const [text, setText] = useState("");
    const [files, setFiles] = useState([]);
    const [posting, setPosting] = useState(false);
    const fileInputRef = useRef();

    const submit = async () => {
        const v = text.trim();
        if (!v) { notify("댓글 내용을 입력하세요", "error"); return; }
        if (posting) return;   // 중복 클릭 가드 — 두 번 눌러 같은 댓글이 두 개 달리는 일을 막는다
        setPosting(true);
        try {
            const created = await boardApi.createComment(postId, v);
            // 첨부는 댓글이 만들어진 뒤에야 붙일 수 있다(commentId 필요) — 순차 업로드.
            for (const f of files) await boardApi.uploadAttachment(postId, f, created?.id);
            setText(""); setFiles([]);
            onRefresh();
        } catch (err) {
            notify(getApiErrorMessage(err, "댓글 작성 실패"), "error");
        } finally {
            setPosting(false);
        }
    };

    return (
        <Stack direction="row" spacing={1.5} sx={{ px: 2, pt: 1.5, pb: 2 }}>
            <Avatar sx={{ width: 32, height: 32, fontSize: 13, flexShrink: 0, bgcolor: C.primarySoft }}>
                {myName?.[0] ?? "나"}
            </Avatar>
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box sx={{ position: "relative" }}>
                    <TextField
                        multiline minRows={3} fullWidth
                        placeholder="댓글 입력"
                        value={text}
                        onChange={e => setText(e.target.value.slice(0, MAX_LEN))}
                        disabled={posting}
                        sx={{
                            "& .MuiOutlinedInput-root": {
                                fontSize: 13.5, bgcolor: C.surface, alignItems: "flex-start",
                                // 우하단 카운터와 글자가 겹치지 않게 아래 여백을 확보한다.
                                pb: 3,
                            },
                        }}
                    />
                    <Typography sx={{
                        position: "absolute", right: 12, bottom: 10,
                        fontSize: 11.5, color: text.length >= MAX_LEN ? C.error : C.textMuted,
                        pointerEvents: "none",
                    }}>
                        {text.length} / {MAX_LEN}
                    </Typography>
                </Box>

                {files.length > 0 && (
                    <Stack direction="row" flexWrap="wrap" gap={0.75} mt={0.75}>
                        {files.map((f, i) => (
                            <Stack key={i} direction="row" alignItems="center" spacing={0.5}
                                sx={{
                                    px: 1, py: 0.3, borderRadius: 1,
                                    border: `1px solid ${C.border}`, bgcolor: C.surface, maxWidth: 240,
                                }}>
                                <Typography noWrap sx={{ fontSize: 12, color: C.textSecondary }}>{f.name}</Typography>
                                <IconButton size="small" sx={{ p: 0.1 }}
                                    onClick={() => setFiles(files.filter((_, j) => j !== i))}>
                                    <DeleteRoundedIcon sx={{ fontSize: 13, color: C.textMuted }} />
                                </IconButton>
                            </Stack>
                        ))}
                    </Stack>
                )}

                <Stack direction="row" alignItems="center" spacing={0.75} mt={1}>
                    <ActionButton variant="primary" startIcon={false}
                        onClick={submit} loading={posting} disabled={!text.trim()}
                        sx={{ fontSize: 12.5 }} label="등록" />
                    <Tooltip title="파일 첨부">
                        <span>
                            <IconButton size="small" disabled={posting}
                                onClick={() => fileInputRef.current?.click()} sx={{ p: 0.5 }}>
                                <AttachFileRoundedIcon sx={{ fontSize: 17, color: C.textMuted }} />
                            </IconButton>
                        </span>
                    </Tooltip>
                    <input ref={fileInputRef} type="file" hidden multiple
                        onChange={e => setFiles(p => [...p, ...Array.from(e.target.files)])} />
                </Stack>
            </Box>
        </Stack>
    );
}
